import {
  combineLatest,
  distinctUntilChanged,
  map,
  tap,
  type Observable,
} from 'rxjs';
import { spreadMap } from '@ardoq/rxbeach';
import type { I18nLocale } from '@ardoq/i18n';
import {
  type APIOrganizationUser,
  type ArdoqId,
  type GroupingRule,
  ViewIds,
} from '@ardoq/api-types';
import type { LoadedGraphWithViewpointMode } from '@ardoq/graph';
import type { GraphComponentViewLegendProps } from '@ardoq/view-legend';
import { usersOperations } from '@ardoq/core';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import type { Locale } from '@ardoq/locale';
import { orgUsersById$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { graphGroupingRules$ } from 'streams/grouping/graphGroupingRules$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import type {
  BlocksGraph,
  BlocksLinksAggregation,
} from 'tabview/visualizations/aq-blocks/types';
import { isEqual } from 'lodash';
import {
  assistantBlocks$,
  type AssistantBlocksState,
  ASSISTANT_BLOCKS_ZOOM_ACTIONS,
} from './assistantBlocks$';
import { toViewPaneZoomActions } from 'tabview/assistantShared/useZoomActions';
import {
  type BlocksArrangement,
  parseArrangements,
  parseTitle,
} from './arrangementRecipe';
import type { AssistantBlocksViewSettings } from './types';
import { buildBlocksGraph } from './buildBlocksGraph';
import {
  buildStyleByModelId,
  type ModelIdStyle,
} from 'tabview/assistantShared/buildStyleByModelId';
import {
  type ReferenceLineSettings,
  toLinksAggregation,
} from 'tabview/assistantShared/referenceLines';
import type { LabelFormattingFilter } from 'tabview/assistantShared/buildBlocksGraph';
import {
  EMPTY_LEGEND_PROPS,
  toLegendProps,
} from 'tabview/assistantShared/legend';
import { registerViewPane } from 'tabview/assistantShared/registerViewPane';
import {
  graphHasGroups,
  parseCollapsedGroupIds,
} from 'tabview/assistantShared/collapsedGroups';

export type AssistantBlocksViewModel = {
  i18nLocale: I18nLocale;
  /** The grid to render, or null when the view holds no components. */
  graph: BlocksGraph | null;
  /** The latest layout title from the assistant, shown above the grid. */
  title: string | null;
  /** A failure to surface in place of the empty state. */
  error: string | null;
  isLegendActive: boolean;
  legendProps: GraphComponentViewLegendProps;
  /** How reference lines are drawn: combined by type, separate, or hidden. */
  linksAggregation: BlocksLinksAggregation;
  /** Changes whenever the arrangement or grouping does, which is when the view re-fits its camera. */
  layoutKey: string;
  /** The saved closed groups, which the view keeps in step with the canvas. */
  collapsedGroupIds: string[];
};

/** The settings the view pane header offers. */
export type AssistantBlocksDisplaySettings = ReferenceLineSettings;

/** What the view pane header reads: the view model, its settings, and whether it has groups to fold. */
export type AssistantBlocksViewProperties = AssistantBlocksViewModel & {
  hasGroups: boolean;
  viewSettings: AssistantBlocksDisplaySettings;
};

/** Everything but the legend toggle, the reference lines and the saved collapse, which must not rebuild the graph. */
type GraphViewModel = Omit<
  AssistantBlocksViewModel,
  'isLegendActive' | 'linksAggregation' | 'collapsedGroupIds'
>;

/** The stored arrangement and title, with the latest failure. */
export type AssistantBlocksLayout = {
  arrangements: BlocksArrangement[];
  title: string | null;
  collapsedGroupIds: string[];
  error: string | null;
};

type ToGraphViewModelArgs = {
  layout: AssistantBlocksLayout;
  i18nLocale: I18nLocale;
  locale: Locale;
  loadedGraph: LoadedGraphWithViewpointMode;
  groupingRules: GroupingRule[];
  formattingFilters: LabelFormattingFilter[];
  styleByModelId: Map<ArdoqId, ModelIdStyle>;
  orgUsersById: Record<ArdoqId, APIOrganizationUser>;
};

const toLayoutKey = (
  { arrangements }: AssistantBlocksLayout,
  groupingRules: GroupingRule[]
): string => JSON.stringify({ arrangements, groupingRules });

export const toGraphViewModel = ({
  layout,
  i18nLocale,
  locale,
  loadedGraph,
  groupingRules,
  formattingFilters,
  styleByModelId,
  orgUsersById,
}: ToGraphViewModelArgs): GraphViewModel => {
  const layoutKey = toLayoutKey(layout, groupingRules);
  const { graph, referenceTypes, componentIds } = buildBlocksGraph({
    loadedGraph,
    styleByModelId,
    arrangements: layout.arrangements,
    collapsedGroupIds: layout.collapsedGroupIds,
    locale,
    groupingRules,
    formattingFilters,
    formatUserFieldFn: val =>
      usersOperations.formatUserField(orgUsersById, val),
    i18nLocale,
  });

  // Nothing in scope, or the traversal rejected the graph model and returned an
  // empty result: render the empty state rather than a blank grid and a legend.
  if (!graph.root.children?.length) {
    return {
      i18nLocale,
      graph: null,
      title: layout.title,
      error: layout.error,
      legendProps: EMPTY_LEGEND_PROPS,
      layoutKey,
    };
  }

  return {
    i18nLocale,
    graph,
    title: layout.title,
    error: layout.error,
    legendProps: toLegendProps({
      scopeComponentIds: componentIds,
      referenceTypes,
      displayShapesInsteadOfIcons: false,
    }),
    layoutKey,
  };
};

/** Stored settings arrive unchecked from user settings, viewpoints, slides and bookmarks. */
export const toDisplaySettings = ({
  separateReferences,
  hideReferences,
}: AssistantBlocksViewSettings): AssistantBlocksDisplaySettings => ({
  separateReferences: separateReferences === true,
  hideReferences: hideReferences === true,
});

export const toLayout = (
  { arrangements, title, collapsedGroupIds }: AssistantBlocksViewSettings,
  { error }: AssistantBlocksState
): AssistantBlocksLayout => ({
  arrangements: parseArrangements(arrangements),
  title: parseTitle(title),
  collapsedGroupIds: parseCollapsedGroupIds(collapsedGroupIds),
  error,
});

/**
 * Whether two layouts build the same graph. `collapsedGroupIds` is left out: the collapse and
 * expand actions fold the groups on screen, and a rebuild as well would replace the element's
 * graph and cut its animation. The graph still reads the saved value whenever it is rebuilt for
 * another reason.
 */
export const isSameGraphLayout = (
  prev: AssistantBlocksLayout,
  next: AssistantBlocksLayout
): boolean =>
  prev.title === next.title &&
  prev.error === next.error &&
  isEqual(prev.arrangements, next.arrangements);

const VIEW_ID = ViewIds.ASSISTANT_BLOCKS;

const viewSettingsWithChanges$ = getViewpointModeViewSettingsWithChanges$(
  VIEW_ID,
  toViewpointModeMainViewInstanceId(VIEW_ID)
);

const layout$ = combineLatest([
  viewSettingsWithChanges$.pipe(map(settings => settings.currentSettings)),
  assistantBlocks$,
]).pipe(spreadMap(toLayout), distinctUntilChanged(isEqual));

const displaySettings$ = viewSettingsWithChanges$.pipe(
  map(settings => toDisplaySettings(settings.currentSettings)),
  distinctUntilChanged<AssistantBlocksDisplaySettings>(isEqual)
);

const graphViewModel$: Observable<GraphViewModel> = combineLatest([
  layout$,
  i18nLocale$,
  locale$,
  loadedGraph$,
  graphGroupingRules$,
  formattingAndLabelRules$,
  orgUsersById$,
]).pipe(
  distinctUntilChanged(
    (
      [
        prevLayout,
        prevI18nLocale,
        prevLocale,
        prevLoadedGraph,
        prevGroupingRules,
        prevFormattingRules,
        prevOrgUsersById,
      ],
      [
        layout,
        i18nLocale,
        locale,
        loadedGraph,
        groupingRules,
        formattingRules,
        orgUsersById,
      ]
    ) =>
      isSameGraphLayout(prevLayout, layout) &&
      prevI18nLocale === i18nLocale &&
      prevLocale === locale &&
      prevLoadedGraph.scopeData === loadedGraph.scopeData &&
      prevGroupingRules === groupingRules &&
      prevFormattingRules === formattingRules &&
      prevOrgUsersById === orgUsersById
  ),
  map(
    ([
      layout,
      i18nLocale,
      locale,
      loadedGraph,
      groupingRules,
      formattingRules,
      orgUsersById,
    ]) =>
      toGraphViewModel({
        layout,
        i18nLocale,
        locale,
        loadedGraph,
        groupingRules,
        formattingFilters: formattingRules.rules,
        styleByModelId: buildStyleByModelId(
          loadedGraph.scopeData,
          formattingRules.rules,
          'icon'
        ),
        orgUsersById,
      })
  )
);

const collapsedGroupIds$ = layout$.pipe(
  map(({ collapsedGroupIds }) => collapsedGroupIds),
  distinctUntilChanged<string[]>(isEqual)
);

export const viewModel$: Observable<AssistantBlocksViewModel> = combineLatest([
  graphViewModel$,
  viewSettingsWithChanges$.pipe(
    map(settings => settings.currentSettings.isLegendActive),
    distinctUntilChanged()
  ),
  collapsedGroupIds$,
  displaySettings$,
]).pipe(
  map(
    ([graphViewModel, isLegendActive, collapsedGroupIds, displaySettings]) => ({
      viewModel: {
        ...graphViewModel,
        isLegendActive,
        linksAggregation: toLinksAggregation(displaySettings),
        collapsedGroupIds,
      },
      displaySettings,
    })
  ),
  tap(({ viewModel, displaySettings }) =>
    registerViewPane({
      viewId: VIEW_ID,
      viewProperties: {
        ...viewModel,
        hasGroups: graphHasGroups(viewModel.graph),
        viewSettings: displaySettings,
      } satisfies AssistantBlocksViewProperties,
      isLegendActive: viewModel.isLegendActive,
      ...toViewPaneZoomActions(ASSISTANT_BLOCKS_ZOOM_ACTIONS),
    })
  ),
  map(({ viewModel }) => viewModel)
);
